import { CommonModule } from '@angular/common';
import { Component, Inject, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { StartManualAutomationDialogComponent, StartManualAutomationModule } from '@galaxy/automata/shared';
import { COMPANY_PROFILE_CARD_DATA_TOKEN } from '@galaxy/crm/static';
import { TranslateModule } from '@ngx-translate/core';
import { Activity, AutomataService, Automation, Context, EntityType, PagedResponse, Stage } from '@vendasta/automata';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { catchError, combineLatest, firstValueFrom, map, Observable, of, switchMap } from 'rxjs';
import { CompanyAccountService } from '../../../company-account.service';
import { PartnerCRMAccessService } from '../../../partner-crm-access.service';
import { toSignal } from '@angular/core/rxjs-interop';

const pageSize = 5;
const queryParamsManualTrigger = encodeURIComponent(
  '{"trigger":"AUTOMATIONS.EDITOR.TRIGGERS.MANUAL_ACCOUNT_TRIGGER.TITLE"}',
);
const routeRoot = '/automations';

@Component({
  selector: 'app-automations',
  imports: [
    CommonModule,
    MatExpansionModule,
    MatCardModule,
    MatTooltipModule,
    MatButtonModule,
    RouterModule,
    TranslateModule,
    GalaxyLoadingSpinnerModule,
    MatIconModule,
    StartManualAutomationModule,
  ],
  templateUrl: './automations.component.html',
  styleUrls: ['../association-panel-base-styles.component.scss', './automations.component.scss'],
})
export class AutomationsComponent {
  latestAutomations$: Observable<Automation[]>;
  protected creatingAccountGroup$: Observable<boolean>;

  private readonly data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);
  private readonly automationService = inject(AutomataService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly router = inject(Router);
  private readonly accountCreationService = inject(CompanyAccountService);
  private readonly dialog = inject(MatDialog);
  private readonly accessService = inject(PartnerCRMAccessService);

  protected accountGroupId$ = this.accountCreationService.accountGroupId$;
  protected readonly canModifyAutomations = toSignal(this.accessService.canModifyAutomations$);

  constructor(@Inject('PARTNER_ID') private readonly partnerId$: Observable<string>) {
    this.setupAutomationsFetching();
    this.creatingAccountGroup$ = this.accountCreationService.createAccountGroupLoading$(this.data.company_id);
  }

  private setupAutomationsFetching(): void {
    this.accountCreationService.setAccountGroupId(this.data.account_group_id ?? '');
    const automationIDs$ = combineLatest([this.partnerId$, this.accountGroupId$]).pipe(
      switchMap(([partnerId, accountGroupID]) => {
        if (!partnerId || !accountGroupID) {
          return of(null);
        }
        const automationActivityFilter = {
          latestPerAutomation: true,
          partnerId: partnerId,
          entityId: accountGroupID,
          stage: [
            Stage.STAGE_WORKFLOW_STEP,
            Stage.STAGE_WORKFLOW_COMPLETED,
            Stage.STAGE_WORKFLOW_CANCELED,
            Stage.STAGE_WORKFLOW_COMPLETED_FROM_ERROR,
            Stage.STAGE_WORKFLOW_COMPLETED_FROM_GOAL,
          ],
        };
        // TODO [SNP-2345]: add in page cursor parameter for when we add the ability to list more than 5 automations in the panel
        return this.automationService.listActivities(automationActivityFilter, '', pageSize);
      }),
      catchError(() => {
        this.snackbarService.openErrorSnack('CRM.ERROR.LOAD_AUTOMATIONS_FAILED');
        return null;
      }),
      map((resp: PagedResponse<Activity>) => {
        if (resp?.results && resp.results.length > 0) {
          return resp.results.map((activity) => {
            return activity.automationId;
          });
        }
        return null;
      }),
    );

    this.latestAutomations$ = combineLatest([this.partnerId$, automationIDs$]).pipe(
      switchMap(([partnerId, automationIds]) => {
        if (!partnerId || !automationIds) {
          return of([]);
        }
        return this.automationService.getMultiAutomations(partnerId, '', automationIds);
      }),
    );
  }

  handleViewAllClick(): void {
    this.router.navigate([routeRoot], {
      queryParams: { automations: queryParamsManualTrigger },
    });
  }

  handleAutomationClick(automationId: string): void {
    firstValueFrom(this.accountGroupId$).then((accountGroupID) => {
      if (accountGroupID) {
        this.router.navigate([routeRoot, 'automation', automationId, 'activity'], {
          queryParams: { activities: `{"accountGroup":"${accountGroupID}"}` },
        });
      }
    });
  }

  async onTriggerAutomationButtonClicked(): Promise<void> {
    const config = await firstValueFrom(this.getDialogConfig$());
    if (config) {
      this.dialog.open(StartManualAutomationDialogComponent, config);
    }
  }

  private getDialogConfig$(): Observable<MatDialogConfig> {
    return combineLatest([
      this.partnerId$,
      this.accountCreationService.fetchAccountGroupId$(this.data.company_id),
      this.accessService.canModifyAutomations$,
      this.accessService.hasPartnerPermissions$,
    ]).pipe(
      map(([partnerId, accountGroupId, canModifyAutomations, hasAdminPermissions]) => {
        const config = new MatDialogConfig();

        config.data = {
          namespace: partnerId,
          options: {
            hideSettingsLink: !canModifyAutomations,
            onlySalespersonAutomations: !hasAdminPermissions,
          },
          entities: new Map<EntityType, string[]>([
            [EntityType.ENTITY_TYPE_ACCOUNT_GROUP, [accountGroupId]],
            [EntityType.ENTITY_TYPE_COMPANY, [this.data.company_id]],
          ]),
          context: Context.AUTOMATION_CONTEXT_PARTNER,
        };
        return config;
      }),
    );
  }
}
